import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  SkeletonText,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useToast } from "../../hooks/useToast";

const formatAmount = (amount?: number, currency?: string | null) => {
  if (amount == null) return "-";
  const formatted = amount.toFixed(2);
  return currency ? `${currency} ${formatted}` : formatted;
};

export const CherryPayCardTransactions = () => {
  const { cardId } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const apiClient = useAdminApiClient();

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [csvLoading, setCsvLoading] = useState(false);

  const params = {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  const { data: card } = useAdminApiRequest(
    (apiClient) => apiClient.getCherryPayCard(cardId!),
    [cardId]
  );

  const externalAccountId = card?.ExternalAccountId ?? cardId;

  const {
    items: transactions,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    (apiClient) => apiClient.getCherryPayCardTransactions(cardId!, params),
    (apiClient, _, continuationToken) =>
      apiClient.getCherryPayCardTransactions(cardId!, {
        ...params,
        continuationToken,
      }),
    [cardId, startDate, endDate]
  );

  const handleCsvDownload = useCallback(async () => {
    if (!cardId) return;
    setCsvLoading(true);
    const result = await apiClient.getCherryPayCardTransactionsCsv(cardId, {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    setCsvLoading(false);
    if (result.ok && result.data) {
      const url = URL.createObjectURL(result.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cherrypay-card-${externalAccountId}-transactions_${
        startDate || "all"
      }-to-${endDate || "all"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccessToast("CSV downloaded.");
    } else if (!result.ok) {
      showErrorToast(result.message ?? "Failed to download CSV.");
    }
  }, [
    apiClient,
    cardId,
    startDate,
    endDate,
    externalAccountId,
    showSuccessToast,
    showErrorToast,
  ]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Card Transactions</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/admin/cherrypay-cards">
                CherryPay Cards
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/cherrypay-cards/${cardId}`}>
                {cardId}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/cherrypay-cards/${cardId}/transactions`}
              >
                Transactions
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <HStack w="100%" spacing={4} pb={4} flexWrap="wrap">
            <FormControl maxW="180px">
              <FormLabel>Start Date</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel>End Date</FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormControl>
            <Box pt={8}>
              <Button
                colorScheme="cherryButton"
                onClick={handleCsvDownload}
                isLoading={csvLoading}
              >
                Download CSV
              </Button>
            </Box>
          </HStack>

          <Box w="100%" overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th whiteSpace="nowrap">Transaction ID</Th>
                  <Th whiteSpace="nowrap">Parent ID</Th>
                  <Th whiteSpace="nowrap">Type</Th>
                  <Th whiteSpace="nowrap">Type Description</Th>
                  <Th whiteSpace="nowrap">Account ID</Th>
                  <Th whiteSpace="nowrap">Occurred At</Th>
                  <Th whiteSpace="nowrap">Amount</Th>
                  <Th whiteSpace="nowrap">Fee</Th>
                  <Th whiteSpace="nowrap">Reference</Th>
                  <Th whiteSpace="nowrap">Running Balance</Th>
                  <Th whiteSpace="nowrap">Merchant Category Code</Th>
                  <Th whiteSpace="nowrap">Acquirer ID</Th>
                  <Th whiteSpace="nowrap">Card Acceptor ID</Th>
                  <Th whiteSpace="nowrap">Terminal ID</Th>
                  <Th whiteSpace="nowrap">Card Acceptor Location</Th>
                </Tr>
              </Thead>
              <Tbody>
                {!isLoading &&
                  (transactions ?? []).map((tx, idx) => {
                    const transactionId = tx.id ?? `tx-${idx}`;
                    return (
                      <Tr key={transactionId}>
                        <Td whiteSpace="nowrap">{transactionId}</Td>
                        <Td whiteSpace="nowrap">{tx.parent_id ?? "-"}</Td>
                        <Td whiteSpace="nowrap">{tx.type ?? "-"}</Td>
                        <Td whiteSpace="nowrap">
                          {tx.type_description ?? "-"}
                        </Td>
                        <Td whiteSpace="nowrap">{tx.account_id ?? "-"}</Td>
                        <Td whiteSpace="nowrap">{tx.occurred_at ?? "-"}</Td>
                        <Td whiteSpace="nowrap">
                          {formatAmount(tx.amount, tx.amount_currency)}
                        </Td>
                        <Td whiteSpace="nowrap">
                          {formatAmount(tx.fee_amount, tx.fee_amount_currency)}
                        </Td>
                        <Td whiteSpace="nowrap">{tx.reference ?? "-"}</Td>
                        <Td whiteSpace="nowrap">
                          {formatAmount(
                            tx.running_balance_amount,
                            tx.running_balance_amount_currency
                          )}
                        </Td>
                        <Td whiteSpace="nowrap">
                          {tx.merchant_category_code ?? "-"}
                        </Td>
                        <Td whiteSpace="nowrap">{tx.acquirer_id ?? "-"}</Td>
                        <Td whiteSpace="nowrap">
                          {tx.card_acceptor_id ?? "-"}
                        </Td>
                        <Td whiteSpace="nowrap">{tx.terminal_id ?? "-"}</Td>
                        <Td whiteSpace="nowrap">
                          {tx.card_acceptor_location ?? "-"}
                        </Td>
                      </Tr>
                    );
                  })}
                {transactions?.length === 0 && !isLoading && (
                  <Tr>
                    <Td colSpan={15} textAlign="center">
                      No transactions found
                    </Td>
                  </Tr>
                )}
                {isLoading && (
                  <Tr>
                    <Td colSpan={15}>
                      <SkeletonText noOfLines={8} spacing="4" />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>

          {fetchNextPage && !isLoading && (
            <Box w="100%">
              {!isLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {isLoadingPage && (
                <SkeletonText mt="4" noOfLines={4} spacing="4" />
              )}
            </Box>
          )}

          {error != null && (
            <ErrorMessage>
              {error ?? "An error was encountered while loading transactions."}
            </ErrorMessage>
          )}
          {(!transactions || isLoading) && <LoadingIndicator />}
        </VStack>
      </PageContent>
    </>
  );
};
